<template>
  <div class="h-full">
    <div class="mx-1">
      <SymbolSearch
        wrapper-class="w-[20%]"
        show-postfix
        :is-show-market-depth="isShowMarketDepth"
        is-show-recent-search
        :active-symbol-name="activeSymbolName"
        :market-depth-disabled="isOptionChainTabSelected"
        @update-symbol="(symbol) => updateActiveIsin(symbol.isin)"
        @handle-market-depth="handleMarketDepthVisibility(!isShowMarketDepth)"
      />
    </div>

    <div class="flex h-[calc(100%-46px)] mx-1 relative">
      <div
        class="flex-col bg-neutral-secondary rounded-md w-full h-full relative"
        :class="[
          isShowMarketDepth ? 'min-w-[calc(100%-30rem)]' : 'w-full',
          !vertical ? 'overflow-hidden' : 'overflow-auto',
        ]"
      >
        <div class="h-[calc(100%-46px)]">
          <ComponentState
            v-if="selectedTab === SYMBOL_COMPONENTS_TABS.detail.id"
            :fetch-status="symbolFetchStatus"
          >
            <template #loading>
              <SymbolSkeleton />
            </template>

            <template #error>
              <Error class="p-2" @retry="() => emit('getSymbol')" />
            </template>

            <SymbolDetail
              :symbol="symbol"
              :portfolio-symbols="portfolioSymbols"
              :vertical="vertical"
            />
          </ComponentState>

          <SymbolTechnicalChart
            v-if="selectedTab === SYMBOL_COMPONENTS_TABS.chart.id"
            :symbol="symbol"
          />

          <IndustryGroupsTable
            v-if="selectedTab === SYMBOL_COMPONENTS_TABS.industryGroups.id"
            :isin="isin"
            :symbol="symbol"
          />

          <OptionChainTable
            v-if="
              selectedTab === SYMBOL_COMPONENTS_TABS.optionChain.id && isOption
            "
            :isin="isin"
            :symbol="symbol"
          />
        </div>

        <div
          class="bg-neutral-secondary w-full rounded-b-md absolute left-0 bottom-0 pt-2 pb-1"
        >
          <NButtonToggleGroup
            align="center"
            :model-value="selectedTab"
            color="grayGreen"
            size="middle"
            rounded="medium"
          >
            <template v-for="(tab, key) in SYMBOL_COMPONENTS_TABS" :key="key">
              <NButtonToggle
                v-if="tab.isOptionٍElement ? isOption : true"
                v-gid="{
                  module: 'symbol',
                  screen: 'home',
                  section: 'symbolDetail',
                  component: tab.gidComponent,
                }"
                :model-value="SYMBOL_COMPONENTS_TABS[key]"
                :label="tab.label"
                :badge="tab.badge"
                class="w-full whitespace-nowrap"
                @click="handleTabChange(tab.id)"
              >
                <template #icon>
                  <component :is="tab.icon" size="20" />
                </template>
              </NButtonToggle>
            </template>
          </NButtonToggleGroup>
        </div>
      </div>

      <div
        v-if="isShowMarketDepth"
        class="bg-neutral-secondary rounded-md basis-[30rem] h-full ms-1"
      >
        <MarketDepth :symbol="symbol" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { isEmpty } from '@libs/utils';
import { useIsFetching } from '@libs/use';
import { useFeatureFlag } from '@libs/feature-flag';
import { Instrument, PortfolioItem } from '@libs/api-reference';
import { QueryStatus } from '@libs/use';
import { isOptionSymbol, SYMBOL_QUERY_KEYS } from '@core/symbol';
import { useLiveGetOptionContract } from '@core/option';

import {
  SymbolDetail,
  SymbolTechnicalChart,
  SymbolSearch,
  MarketDepth,
  IndustryGroupsTable,
  useActiveSymbolIsin,
  SymbolSkeleton,
  SYMBOL_COMPONENTS_TABS,
} from '@/modules/symbol';
import { OptionChainTable } from '@/modules/option';

import eventBus from '@/composables/event/eventBus';

import Error from '@/components/Error.vue';
import ComponentState from '@/components/ComponentState.vue';

type SymbolEmitsType = {
  (e: 'getSymbol'): void;
  (e: 'handleShowTechnicalChart', value: boolean, isExpanded: boolean): void;
  (e: 'handleShowOptionChain', value: boolean): void;
};
type SymbolPropsType = {
  vertical?: boolean;
  portfolioSymbols: PortfolioItem[];
  showTechnicalChart?: boolean;
  symbol: Instrument;
  symbolStatus: QueryStatus;
  isin: string;
  isLoadingSymbol: boolean;
};

const props = withDefaults(defineProps<SymbolPropsType>(), {
  vertical: false,
  portfolioSymbols: () => [],
  isLoadingSymbol: false,
});

const emit = defineEmits<SymbolEmitsType>();

const { t } = useI18n();

const isOption = useFeatureFlag('option');

const isSymbolOption = computed(() =>
  isOptionSymbol(props.symbol.instrumentTypeCode as string)
);

const isOptionChainTabSelected = computed(
  () => selectedTab.value === SYMBOL_COMPONENTS_TABS.optionChain.id
);

const isShowMarketDepth = ref(false);
const selectedTab = ref<number>(SYMBOL_COMPONENTS_TABS.detail.id);

const symbolFetchStatus = computed(() => {
  if (props.isLoadingSymbol) {
    return 'loading';
  } else if (isSymbolOption.value) {
    if (getOptionContractStatus.value === 'error') {
      return 'success';
    }

    return getOptionContractStatus.value;
  } else {
    return props.symbolStatus;
  }
});
const { updateActiveIsin, activeIsin } = useActiveSymbolIsin();

const isFetchingSymbol = useIsFetching({
  queryKey: SYMBOL_QUERY_KEYS.symbol(activeIsin),
});

const { getOptionContractStatus } = useLiveGetOptionContract({
  isin: computed(() => activeIsin.value),
  immediate: computed(() => !isFetchingSymbol.value && isSymbolOption.value),
});

eventBus.on('openIndustryGroups', () => {
  selectedTab.value = SYMBOL_COMPONENTS_TABS.industryGroups.id;
});

const handleTabChange = (newValue) => {
  selectedTab.value = newValue;

  if (isOptionChainTabSelected.value) {
    isShowMarketDepth.value = false;
  }

  emit(
    'handleShowOptionChain',
    newValue === SYMBOL_COMPONENTS_TABS.optionChain.id
  );

  emit(
    'handleShowTechnicalChart',
    newValue !== SYMBOL_COMPONENTS_TABS.detail.id,
    newValue === SYMBOL_COMPONENTS_TABS.chart.id
  );
};

const handleMarketDepthVisibility = (value: boolean) => {
  if (!isOptionChainTabSelected.value) {
    isShowMarketDepth.value = value;
  }
};

const activeSymbolName = computed(() =>
  !isEmpty(props.symbol)
    ? `${props.symbol.instrumentName} - ${props.symbol.instrumentTitle} - ${props.symbol.exchangeName}`
    : ''
);
</script>
